import { useEffect, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import MethodArcs from "@/components/MethodArcs";
import MethodPhaseDrawer from "@/components/MethodPhaseDrawer";
import FrameworkLabelsDialog from "@/components/FrameworkLabelsDialog";
import ContinuousDynamicCard from "@/components/method/ContinuousDynamicCard";
import { PencilSimpleLine, Eye, Compass, TextT } from "@phosphor-icons/react";

const MANAGING_ROLES = new Set(["partner", "principal", "managing_principal", "managing principal"]);

/**
 * The Method — Yadid's firm-wide, client-facing arc.
 *
 * Renders the two symmetric arcs framed by four MP-editable surrounding
 * labels. Clicking a phase opens the drawer (view/edit copy). Managing
 * Principals can also open the FrameworkLabelsDialog to rename the four
 * surrounding dimensions.
 */
export default function Method() {
  const { user } = useAuth();
  const isMP = MANAGING_ROLES.has((user?.role || "").toLowerCase());
  const [template, setTemplate] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState("view");
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  // Continuous-dynamic pin state — null | 'connections' | 'leadership'.
  // Click a top/bottom dynamic label to pin, click same to unpin, click
  // other to switch. Outside-click and Esc also clear.
  const [pinnedDynamic, setPinnedDynamic] = useState(null);
  const cardWrapRef = useRef(null);
  const arcsPanelRef = useRef(null);

  const load = () => api.get("/method/template").then((r) => setTemplate(r.data)).catch(() => toast.error("Could not load method"));
  useEffect(() => { load(); }, []);

  const toggleDynamic = (key) => setPinnedDynamic((prev) => (prev === key ? null : key));
  const closeDynamic = () => setPinnedDynamic(null);

  // Outside-click + Esc dismiss for the pinned card. The arcs panel is
  // excluded from "outside" so clicking a dynamic label to toggle/switch
  // doesn't fight this handler.
  useEffect(() => {
    if (!pinnedDynamic) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeDynamic(); };
    const onDown = (e) => {
      const withinCard = cardWrapRef.current?.contains(e.target);
      const withinArcs = arcsPanelRef.current?.contains(e.target);
      if (!withinCard && !withinArcs) closeDynamic();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [pinnedDynamic]);

  const openPhase = (key) => { setSelectedKey(key); setDrawerOpen(true); };

  const savePhase = async (updatedPhase) => {
    const nextPhases = template.phases.map((p) => (p.key === updatedPhase.key ? { ...p, ...updatedPhase } : p));
    const { data } = await api.put("/method/template", { phases: nextPhases });
    setTemplate({ ...data, framework_labels: template.framework_labels }); // preserve labels
    setDrawerOpen(false);
  };

  const selectedPhase = template?.phases?.find((p) => p.key === selectedKey) || null;

  if (!template) return <div className="p-8 text-sm text-muted-foreground">Loading the method…</div>;

  return (
    <PageShell size="standard" testid="method-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary flex items-center gap-1.5">
            <Compass size={12} weight="fill" /> Our approach
          </div>
          <h1 className="mt-3 font-bold tracking-tight" style={{ fontFamily: "Georgia, serif", fontSize: "3rem", lineHeight: 1.02 }}>
            The Yadid Embrace Method
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground italic" style={{ fontFamily: "Georgia, serif" }}>
            A double-diamond arc, reframed for how we actually work: to <span className="text-foreground">listen</span> before naming,
            to <span className="text-foreground">reveal</span> the true problem, to <span className="text-foreground">reimagine</span> the
            possible futures, and to <span className="text-foreground">guide</span> the strategy through its daily practice — because the
            work of a trusted advisor is not to hand off a plan, but to walk alongside it.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
          {isMP && (
            <Button
              onClick={() => setLabelsDialogOpen(true)}
              variant="outline"
              className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
              data-testid="method-edit-framework-labels"
            >
              <TextT size={14} weight="bold" className="mr-1.5" /> Framework labels
            </Button>
          )}
          <Button
            onClick={() => setMode(mode === "edit" ? "view" : "edit")}
            variant="outline"
            className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
            data-testid="method-toggle-edit"
          >
            {mode === "edit" ? (<><Eye size={14} weight="bold" className="mr-1.5" /> View mode</>) : (<><PencilSimpleLine size={14} weight="bold" className="mr-1.5" /> Edit mode</>)}
          </Button>
        </div>
      </div>

      <div ref={arcsPanelRef} className="border border-border bg-card p-6 md:p-10" data-testid="method-arcs-panel">
        <MethodArcs
          phases={template.phases}
          onSelect={openPhase}
          selected={selectedKey}
          frameworkLabels={template.framework_labels}
          activeDynamic={pinnedDynamic}
          onSelectDynamic={toggleDynamic}
        />
        <p className="mt-6 text-center text-[0.6875rem] font-mono uppercase tracking-[0.24em] text-muted-foreground">
          Tap a phase to open its full copy · {mode === "edit" ? "editing enabled" : "read-only"}
        </p>
      </div>

      {pinnedDynamic && (
        <div ref={cardWrapRef}>
          <ContinuousDynamicCard
            dynamicKey={pinnedDynamic}
            onClose={closeDynamic}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="method-phase-index">
        {template.phases.map((p) => (
          <button
            key={p.key}
            onClick={() => openPhase(p.key)}
            className="text-left border border-border bg-card p-5 hover:border-primary/60 transition-colors"
            data-testid={`method-phase-card-${p.key}`}
          >
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">{p.framework_label} · {p.mindset}</div>
            <div className="font-bold text-2xl mt-1" style={{ fontFamily: "Georgia, serif" }}>{p.client_label}</div>
            <p className="text-sm italic text-muted-foreground mt-2 leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>
              {p.intent}
            </p>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-3">
              {p.activities.length} activities · {p.deliverables.length} deliverables
            </div>
          </button>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-end">
        <Link
          to="/method/reflection-balance"
          data-testid="method-reflection-balance-link"
          className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-brass border-b border-transparent hover:border-brass transition-colors"
        >
          View your reflection balance →
        </Link>
      </div>

      <MethodPhaseDrawer
        open={drawerOpen}
        onOpenChange={(v) => {
          setDrawerOpen(v);
          // Clear the selection when the drawer closes so the underline
          // beneath the phase name disappears with it. Without this the
          // last-clicked node stays visually "selected" indefinitely.
          if (!v) setSelectedKey(null);
        }}
        phase={selectedPhase}
        mode={mode === "edit" ? "edit" : "view"}
        onSave={savePhase}
      />
      <FrameworkLabelsDialog
        open={labelsDialogOpen}
        onOpenChange={setLabelsDialogOpen}
        current={template.framework_labels}
        onSaved={(fresh) => setTemplate(fresh)}
      />
    </PageShell>
  );
}
